import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject, of, from } from 'rxjs';
import {
	catchError,
	concatMap,
	delay,
	map,
	retry,
	retryWhen,
	takeUntil,
} from 'rxjs/operators';

import { IEventListener, EventManagerService } from '@global/event-manager.service';
import { uid } from '@helpers/utils';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor, IEventListener {

	private _uuid: string = uid();
	get uuid(): string { return this._uuid; }

	private abortPendingHTTPRequests$: Subject<void> = new Subject<void>();
	private maxAttempts: number = 5;
  private retryWaitMilliSeconds: number = 1000;
	private countConnectionLost: number = 0;

	constructor(private eventManager: EventManagerService) {
		this.eventManager.registerEvent('abort-http-request', this, (args: any) => {
				this.abortPendingRequests();
			}
		);
	}

	ngOnDestroy(): void {
		this.eventManager.unregisterEvent('abort-http-request', this);
	}

	abortPendingRequests() {
		console.log('Abort pending HTTP requests');
		this.abortPendingHTTPRequests$.next();
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(

			// observable used to abort the request. Careful, parallel requests would all be aborted
			takeUntil(this.abortPendingHTTPRequests$.asObservable()),

			// retry a few times if there's no network
			retry({
				count: this.maxAttempts,
				resetOnSuccess: true,
				delay: (error: any, retryCount: number) => {
					return of(error).pipe(
						concatMap((error: HttpErrorResponse, retryCount: number) => {

							if (error.status == 0) {
								this.countConnectionLost++;
								// interrupted upload, don't retry, but set a custom status code and throw
								if (request.body instanceof FormData) {
									throw new HttpErrorResponse({
										status: 470,
									});
								}
								// otherwise, retry a few times
								console.log('Connection lost', this.countConnectionLost);
								console.log('Retrying', `${retryCount}/${this.maxAttempts}`)
								return of(error);
							}
							// all retries failed, throw
							return throwError(error);
						}),
						delay(this.retryWaitMilliSeconds)
					);
				}

			}),

			// handle custom errors, if API returns 200 but with errors in the body
			// map((event: HttpResponse<any>) => {
			map((event: any) => {
				if (event.body && event.body.error) {
					throw new HttpErrorResponse({
						error: event.body.error,
						headers: event.headers,
						status: event.status,
						url: event.url || undefined
					})
				}
				else if (event.body instanceof Blob && event.body.type === 'application/json') {
					// custom error when the api returned a json when we were expecting a blob
					throw new HttpErrorResponse({
						error: event.body,
						headers: event.headers,
						status: 499,
						url: event.url || undefined
					})
				}
				else {
					return event;
				}
			}),

			// handle the actual errors
			catchError((err: HttpErrorResponse) => {

				console.error('ErrorInterceptor', err);

				// special case of blobs
				if (
					err instanceof HttpErrorResponse
					&& err.error instanceof Blob
					&& err.error.type === 'application/json'
				) {
					// https://github.com/angular/angular/issues/19888
					// When request of type Blob, the error is also in Blob instead of object of the json data
					return new Promise<any>((resolve, reject) => {
						let reader = new FileReader();
						reader.onloadend = (e: Event) => {
							try {
								const errmsg = JSON.parse((<any>e.target).result);
								const readableError = new HttpErrorResponse({
									error: errmsg.error || errmsg,
									headers: err.headers,
									status: err.status,
									statusText: err.statusText,
									url: err.url || undefined
								});
								this.toastError(readableError);
								reject(readableError);
							}
							catch (e) {
								reject(err);
							}
						};
						reader.onerror = (e) => {
							reject(err);
						};
						reader.readAsText(err.error);
					});
				}
				else {
					this.toastError(err);
					return throwError(err);
				}

			})
		)
	}

	toastError(httpResponse: HttpErrorResponse) {
		let error = null;
		if (httpResponse.error && httpResponse.error.message) {
			error = httpResponse.error;
		}
		else if (httpResponse.error && httpResponse.error.error) {
			error = httpResponse.error.error;
		}
		let message = (error && error.message)? error.message : null;

		switch(httpResponse.status){
			case -1:
				message = message || 'Erreur de connexion réseau';
				break;

			case 0:
				message = message || 'Perte de connexion réseau';
				break;

			case 400:
				message = message || 'Requête invalide';
				break;

			case 401:
			if (error.message == 'Expired JWT Token') message = null;
				this.eventManager.emit('logout');
				break;

			case 403:
				message = message || 'Action non autorisée';
				break;

			case 404:
				message = message || 'La ressource demandée n\'existe pas.';
				break;

			case 405:
				message = message || 'Méthode non autorisée';
				break;

			case 408:
				message = message || 'Le serveur a mis trop de temps à repondre';
				break;

			case 414:
				message = message || 'La requête contient trop de paramètres';
				break;

			case 470: // interrupted upload
				message = message || 'Erreur de connexion réseau, transfert interrompu';
				break;

			case 503: // temporary error
				if (httpResponse.error.reason && httpResponse.error.reason == 'maintenance') {
					message = 'Le service est en maintenance. Désolé pour ce désagrément. Nous revenons au plus vite.';
				}
				else {
					message = message || 'Service momentanément indisponible';
				}
				break;

			default: {
				message = message || 'Erreur non répertoriée';
			}
		}

		if (message) {
			this.eventManager.emit('toast', {severity: 'error', summary: 'Erreur', detail: message});
			if (httpResponse.error.data) {
				this.toastDetails(httpResponse.error.data);
			}
		}
	}

	toastDetails(data: any) {
		if (data && data.depends) {
			this.eventManager.emit('toast', {
				severity: 'info',
				summary: 'Objet dépendant',
				detail: data.depends,
				life: 10000
			});
		}
	}

}
